import { Box, Button, IconButton, Theme } from "@mui/material";
import MFSelectField from "../../../lib/formik/SelectField";
import {
    FormikContextType,
    useFormikContext
} from "formik";
import DocumentUpload from "../../../lib/formik/DocumentUpload";
import { DeleteIcons } from "../../../assets/SvgIcons";
import {
    Dispatch,
    SetStateAction,
} from "react";

const documentTypeList = [
    {
        documentTypeLabel: "Order Approval Email",
        documentTypeValue: "Order Approval Email"
    },
    {
        documentTypeLabel: "Call Recordings mp3",
        documentTypeValue: "Call Recordings mp3"
    },
    {
        documentTypeLabel: "Payment Confirmation",
        documentTypeValue: "Payment Confirmation"
    },
    {
        documentTypeLabel: "Bill Receipt",
        documentTypeValue: "Bill Receipt"
    },
    {
        documentTypeLabel: "PPM/ Contribution Agreement",
        documentTypeValue: "PPM/ Contribution Agreement"
    },
    {
        documentTypeLabel: "Others",
        documentTypeValue: "Others"
    }
]

export default function OrderSupportingDocumentForm({
    setLoadingStatus,
    uploadedFileArray,
    setUploadedFileArray,
    isLoading
}: {
    setLoadingStatus: Dispatch<SetStateAction<"" | "apiLoading" | "formSubmitting">>;
    uploadedFileArray: Array<any>
    setUploadedFileArray: Dispatch<SetStateAction<Array<any>>>;
    isLoading: boolean
}): JSX.Element {
    const {
        values,
        setFieldValue
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const handleDocumentUploadStart = () => {
        setLoadingStatus("apiLoading")
    }

    const handleDocumentUploadEnd = () => {
        setLoadingStatus("")
    }

    const handleAddMoreDocument = async () => {
        setUploadedFileArray(prevState => [...prevState, []])

        await setFieldValue("documents", [
            ...(values?.documents ?? []),
            {
                documentId: "",
                documentType: ""
            }
        ])
    }

    const handleDeleteDocument = async (currentDocumentIndex: number) => {
        const modifiedDocuments = (values?.documents ?? []).filter((_: { documentId: string; documentType: string }, index: number) => index !== currentDocumentIndex)

        setUploadedFileArray(prevState => prevState.filter((_: any, index: number) => currentDocumentIndex !== index))

        await setFieldValue("documents", modifiedDocuments)
    }

    const handleUploadedFile = (docIndex: number, file: any) => {
        setUploadedFileArray(prevState => prevState.map((prevData: any, index: number) => docIndex === index ? file : prevData))
    }

    return (
        <Box
            sx={{
                marginTop: "25px",
                display: "flex",
                flexDirection: 'column',
                rowGap: "25px",
            }}
        >
            {values?.documents?.map((
                _: { documentId: string, documentType: string },
                docIndex: number
            ) => {
                const previousSelectedDocumentTypes = values
                    ?.documents
                    ?.filter((_: { documentId: string, documentType: string }, index: number) => docIndex !== index)
                    ?.filter((obj: { documentId: string, documentType: string }) => !!obj?.documentType)
                    ?.map((obj: { documentId: string, documentType: string }) => obj?.documentType)
                    ?? [];

                const modifiedDocumentTypeOptions = documentTypeList
                    ?.filter((obj: {
                        documentTypeLabel: string;
                        documentTypeValue: string;
                    }) => !previousSelectedDocumentTypes.includes(obj?.documentTypeValue))
                    ?? []

                return (
                    <Box key={docIndex}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: 'column',
                                rowGap: "25px"
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    columnGap: "50px"
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <MFSelectField
                                        name={`documents[${docIndex}].documentType`}
                                        label="Select Document Type"
                                        items={documentTypeList.map((obj) => ({
                                            key: obj.documentTypeLabel,
                                            value: obj.documentTypeValue,
                                        }))}
                                        sx={{
                                            "&.MuiOutlinedInput-root": {
                                                "& fieldset>legend": {
                                                    fontSize: "0.78em",
                                                }
                                            }
                                        }}
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            columnGap: "10px"
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                rowGap: "5px",
                                                flex: 1
                                            }}
                                        >
                                            <DocumentUpload
                                                name={`documents[${docIndex}].documentId`}
                                                onFileUpload={handleDocumentUploadStart}
                                                onFileUploaded={handleDocumentUploadEnd}
                                                minFileSize=""
                                                maxFileSize="5 MB"
                                                fileUploadFormats={['image/png', 'image/jpeg', 'application/pdf', 'image/jpg', 'audio/mpeg', 'message/rfc822']}
                                                uploadedFile={uploadedFileArray?.[docIndex] ?? []}
                                                setUploadedFile={(file: any) => handleUploadedFile(docIndex, file)}
                                                disabled={isLoading}
                                            />
                                        </Box>
                                        {values?.documents?.length > 1 && (
                                            <IconButton
                                                sx={{
                                                    padding: "0px 5px 5px",
                                                    alignSelf: "center",
                                                    "&:hover": {
                                                        padding: "5px"
                                                    },
                                                    "&:disabled": {
                                                        opacity: 0.5
                                                    }
                                                }}
                                                onClick={() => handleDeleteDocument(docIndex)}
                                            >
                                                <DeleteIcons />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        flex: 0.5,
                                        display: {
                                            xs: "none",
                                            lg: "block"
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                        {(
                            (values?.documents?.length - 1 === docIndex) &&
                            values?.documents?.length < 5
                        ) && (
                                <Button
                                    sx={(theme: Theme) => ({
                                        ...theme.typography.body_500,
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.primary[500],
                                        backgroundColor: theme.palette.common.white,
                                        border: `1px solid ${theme.palette.primary[500]}`,
                                        borderRadius: "10px",
                                        padding: "15px 40px",
                                        marginTop: "35px",
                                        "&:hover": {
                                            background: theme.palette.primary[50],
                                        },
                                        "&:disabled": {
                                            borderColor: theme.palette.text.disabled,
                                        }
                                    })}
                                    onClick={handleAddMoreDocument}
                                >
                                    + Add More
                                </Button>
                            )}
                    </Box>
                )
            })}
        </Box>
    )
}